import { z } from "zod/v4";
import { leadReelBrandSchema } from "./lead-gen-reel-design";

export const leadAdBrandOverrideSchema = z.object({
  preserveOutfit: z.boolean().optional(),
  video: leadReelBrandSchema.optional(),
  logo: z.object({
    mode: z.enum(["inherit", "custom", "hidden"]),
    url: z.string().min(1).nullable().optional(),
  }),
  accentColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable(),
  /** Null keeps the original green check circles. */
  checkColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  /** Null automatically selects the highest-contrast CTA text color. */
  ctaTextColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  /** False prefers the confirmed licence; without one the Venturu logo appears. */
  preferVenturuLogo: z.boolean().optional(),
  /** Null lets each creative choose its title. Explicit titles are never rewritten. */
  brokerTitle: z.string().trim().max(100).nullable().optional(),
});

export type LeadAdBrandOverride = z.infer<typeof leadAdBrandOverrideSchema>;

export const EMPTY_LEAD_AD_BRAND_OVERRIDE: LeadAdBrandOverride = {
  logo: { mode: "inherit", url: null },
  accentColor: null,
  checkColor: null,
  ctaTextColor: null,
};

/** Missing or invalid JSON means the campaign still follows the organization brand. */
export function parseLeadAdBrandOverride(value: unknown): LeadAdBrandOverride {
  const parsed = leadAdBrandOverrideSchema.safeParse(value);
  if (!parsed.success) return EMPTY_LEAD_AD_BRAND_OVERRIDE;
  return {
    ...parsed.data,
    checkColor: parsed.data.checkColor ?? null,
    ctaTextColor: parsed.data.ctaTextColor ?? null,
  };
}

export function resolveLeadAdLogo(
  organizationLogo: string | null | undefined,
  override: LeadAdBrandOverride | null | undefined,
): string | null {
  const mode = override?.logo.mode ?? "inherit";
  if (mode === "hidden") return null;
  if (mode === "inherit") return organizationLogo ?? null;
  return override?.logo.url ?? null;
}

/** Unset logo and color follow the organization. A custom logo or color does not. */
export function campaignFollowsOrganizationBrand(
  override: LeadAdBrandOverride | null | undefined,
): { logo: boolean; accent: boolean } {
  return {
    logo: (override?.logo.mode ?? "inherit") === "inherit",
    accent: (override?.accentColor ?? null) == null,
  };
}
